import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button,
  CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, FormHelperText, IconButton, InputLabel, MenuItem,
  Select, Stack, Step, StepLabel, Stepper, TextField, Typography, Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  EXTENSION_TYPES,
  fieldsForTypeId,
  resolveTypeIdFromValue,
  typeValueForId,
  isSensitivePath,
  displayPath,
  diffFields,
  wereFieldsRemoved,
} from './ivrExtensionFieldSchemas.js';
import { ivrTypeMeta } from './ivrTypeMeta.js';

const STEPS_CREATE = ['סוג השלוחה', 'פרטים', 'סיכום'];
const STEPS_EDIT = ['הגדרות', 'סיכום'];

function emptyAdvancedFromFields(fields) {
  return Object.entries(fields || {})
    .filter(([k]) => !['type', 'title', 'enter_id'].includes(k))
    .map(([key, value]) => ({ key, value: String(value ?? '') }));
}

/**
 * @param {'create'|'edit'} mode
 * @param {string} parentPath — ליצירה
 * @param {object|null} extension — לעריכה (מ-getExtensionDetail)
 */
export default function IvrExtensionEditorDialog({
  open,
  mode = 'create',
  parentPath = '',
  extension = null,
  saving = false,
  onClose,
  onSave,
}) {
  const isCreate = mode === 'create';
  const steps = isCreate ? STEPS_CREATE : STEPS_EDIT;
  const [activeStep, setActiveStep] = useState(0);

  const initialFields = extension?.ini?.fields || {};
  const [typeId, setTypeId] = useState('menu');
  const [customType, setCustomType] = useState('');
  const [name, setName] = useState('');
  const [form, setForm] = useState({});
  const [advanced, setAdvanced] = useState([]);
  const [confirmPath, setConfirmPath] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setError('');
    setConfirmPath('');
    if (isCreate) {
      setTypeId('menu');
      setCustomType('');
      setName('');
      setForm({ title: '', enter_id: 'no' });
      setAdvanced([]);
    } else {
      const fields = { ...initialFields };
      const tid = resolveTypeIdFromValue(fields.type);
      setTypeId(tid);
      setCustomType(tid === 'other' ? String(fields.type || '') : '');
      setName(extension?.path?.split('/').pop() || '');
      const guidedKeys = new Set(fieldsForTypeId(tid).map((f) => f.key));
      const nextForm = {};
      for (const f of fieldsForTypeId(tid)) {
        nextForm[f.key] = fields[f.key] ?? (f.key === 'enter_id' ? 'no' : '');
      }
      setForm(nextForm);
      // advanced = כל שדה שאינו type ואינו בשדות המודרכים של הסוג הנוכחי
      const adv = Object.entries(fields)
        .filter(([k]) => k !== 'type' && !guidedKeys.has(k))
        .map(([key, value]) => ({ key, value: String(value ?? '') }));
      // גם שדות מודרכים שלא בטופס? כבר בטופס. שומרים גם extras
      if (adv.length === 0 && Object.keys(fields).length) {
        setAdvanced(emptyAdvancedFromFields(fields).filter((r) => !guidedKeys.has(r.key)));
      } else {
        setAdvanced(adv);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, extension?.path]);

  const guidedSchema = useMemo(() => fieldsForTypeId(typeId), [typeId]);

  const resolvedType = typeValueForId(typeId, customType);

  const composedFields = useMemo(() => {
    const out = { ...form };
    if (resolvedType) out.type = resolvedType;
    for (const row of advanced) {
      const k = String(row.key || '').trim();
      if (!k) continue;
      out[k] = String(row.value ?? '');
    }
    return out;
  }, [form, advanced, resolvedType]);

  const targetPath = useMemo(() => {
    if (!isCreate) return extension?.path || '';
    const parent = String(parentPath || '').replace(/^\/+|\/+$/g, '');
    const seg = String(name || '').trim();
    if (!seg) return parent;
    return parent ? `${parent}/${seg}` : seg;
  }, [isCreate, parentPath, name, extension?.path]);

  const needsConfirm = isSensitivePath(targetPath);
  const changes = useMemo(
    () => (isCreate ? diffFields({}, composedFields) : diffFields(initialFields, composedFields)),
    [isCreate, initialFields, composedFields]
  );
  const needReplace = !isCreate && wereFieldsRemoved(initialFields, composedFields);
  const typeChanged = !isCreate && String(initialFields.type || '') !== String(composedFields.type || '');

  const setFormKey = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const validateStep = () => {
    setError('');
    if (isCreate && activeStep === 0) {
      if (typeId === 'other' && !customType.trim()) {
        setError('יש להזין את סוג השלוחה (type)');
        return false;
      }
      if (typeId !== 'other' && !resolvedType) {
        setError('יש לבחור סוג שלוחה');
        return false;
      }
    }
    if ((isCreate && activeStep === 1) || (!isCreate && activeStep === 0)) {
      if (isCreate) {
        if (!String(name).trim()) {
          setError('יש להזין מספר או שם לשלוחה החדשה');
          return false;
        }
        if (/[\\/]/.test(name)) {
          setError('השם לא יכול להכיל קו נטוי');
          return false;
        }
      }
      for (const f of guidedSchema) {
        if (f.required && !String(form[f.key] || '').trim()) {
          setError(`חסר: ${f.label}`);
          return false;
        }
      }
      if (typeId === 'other' && !customType.trim()) {
        setError('חסר סוג השלוחה');
        return false;
      }
    }
    if (activeStep === steps.length - 1) {
      if (needsConfirm && confirmPath.trim().replace(/^\/+/, '') !== String(targetPath).replace(/^\/+/, '')) {
        setError(`נתיב רגיש — הקלידו בדיוק: ${displayPath(targetPath)}`);
        return false;
      }
      if (changes.length === 0 && !isCreate) {
        setError('לא בוצע שינוי');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const handleBack = () => {
    setError('');
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = () => {
    if (!validateStep()) return;
    onSave?.({
      parentPath,
      name: String(name).trim(),
      path: targetPath,
      fields: composedFields,
      mode: needReplace ? 'replace' : 'merge',
      confirmPath: needsConfirm ? String(targetPath) : undefined,
    });
  };

  const onTypePick = (id) => {
    setTypeId(id);
    const nextGuided = fieldsForTypeId(id);
    setForm((prev) => {
      const next = { ...prev };
      for (const f of nextGuided) {
        if (next[f.key] == null) next[f.key] = f.key === 'enter_id' ? 'no' : '';
      }
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {isCreate ? 'שלוחה חדשה' : `עריכת שלוחה ${displayPath(extension?.path)}`}
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2.5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* שלב בחירת סוג — יצירה */}
        {isCreate && activeStep === 0 && (
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              בחרו מה השלוחה עושה — אחר כך נמלא רק את מה שצריך.
            </Typography>
            {EXTENSION_TYPES.map((t) => {
              const meta = ivrTypeMeta(t.typeValue || 'other');
              const selected = typeId === t.id;
              return (
                <Box
                  key={t.id}
                  onClick={() => onTypePick(t.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onTypePick(t.id);
                    }
                  }}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: selected ? meta.color : 'divider',
                    bgcolor: selected ? `${meta.color}10` : 'var(--ck-surface, #fff)',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background 0.15s',
                    '&:hover': { borderColor: meta.color },
                  }}
                >
                  <Typography fontWeight={700}>{t.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                </Box>
              );
            })}
            {typeId === 'other' && (
              <TextField
                label="סוג טכני (type)"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                fullWidth
                size="small"
                helperText="למשל: queue, conference, id_list"
                inputProps={{ dir: 'ltr' }}
                sx={{ mt: 1 }}
              />
            )}
          </Stack>
        )}

        {/* שלב פרטים */}
        {((isCreate && activeStep === 1) || (!isCreate && activeStep === 0)) && (
          <Stack spacing={2}>
            {!isCreate && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`type=${initialFields.type || '—'}`} />
                {typeChanged && (
                  <Chip size="small" color="warning" label="סוג השלוחה ישתנה" />
                )}
              </Stack>
            )}

            {isCreate && (
              <TextField
                label="מספר או שם השלוחה"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                helperText={`תייוצר תחת ${displayPath(parentPath)} כ־${displayPath(targetPath || '…')}`}
                inputProps={{ dir: 'ltr' }}
              />
            )}

            {!isCreate && (
              <FormControl fullWidth size="small">
                <InputLabel>סוג השלוחה</InputLabel>
                <Select
                  label="סוג השלוחה"
                  value={typeId}
                  onChange={(e) => onTypePick(e.target.value)}
                >
                  {EXTENSION_TYPES.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>שינוי סוג עלול לשנות את התנהגות הקו</FormHelperText>
              </FormControl>
            )}

            {!isCreate && typeId === 'other' && (
              <TextField
                label="סוג טכני (type)"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                fullWidth
                size="small"
                inputProps={{ dir: 'ltr' }}
              />
            )}

            {guidedSchema.map((f) => (
              f.input === 'select' ? (
                <FormControl key={f.key} fullWidth size="small">
                  <InputLabel>{f.label}</InputLabel>
                  <Select
                    label={f.label}
                    value={form[f.key] ?? ''}
                    onChange={(e) => setFormKey(f.key, e.target.value)}
                  >
                    {(f.options || []).map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                  {f.help && <FormHelperText>{f.help}</FormHelperText>}
                </FormControl>
              ) : (
                <TextField
                  key={f.key}
                  label={f.label}
                  value={form[f.key] ?? ''}
                  onChange={(e) => setFormKey(f.key, e.target.value)}
                  fullWidth
                  size="small"
                  required={!!f.required}
                  helperText={f.help}
                  inputProps={{ dir: f.dir || 'rtl' }}
                />
              )
            ))}

            <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography fontWeight={700}>מצב מתקדם</Typography>
                  <Typography variant="caption" color="text.secondary">
                    שדות חופשיים כמו ב-ext.ini — רק אם אתם יודעים מה לעשות
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.25}>
                  {advanced.map((row, idx) => (
                    <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                      <TextField
                        label="מפתח"
                        size="small"
                        value={row.key}
                        onChange={(e) => {
                          const next = [...advanced];
                          next[idx] = { ...next[idx], key: e.target.value };
                          setAdvanced(next);
                        }}
                        sx={{ width: 140 }}
                        inputProps={{ dir: 'ltr' }}
                      />
                      <TextField
                        label="ערך"
                        size="small"
                        fullWidth
                        value={row.value}
                        onChange={(e) => {
                          const next = [...advanced];
                          next[idx] = { ...next[idx], value: e.target.value };
                          setAdvanced(next);
                        }}
                        inputProps={{ dir: 'ltr' }}
                      />
                      <IconButton
                        aria-label="הסר שדה"
                        onClick={() => setAdvanced(advanced.filter((_, i) => i !== idx))}
                        color="error"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setAdvanced([...advanced, { key: '', value: '' }])}
                    size="small"
                  >
                    הוסף שדה
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}

        {/* סיכום */}
        {activeStep === steps.length - 1 && (
          <Stack spacing={2}>
            <Alert severity="info">
              {isCreate
                ? `תיווצר שלוחה חדשה בנתיב ${displayPath(targetPath)}`
                : `יעודכנו הגדרות השלוחה ${displayPath(targetPath)}`}
            </Alert>

            {typeChanged && (
              <Alert severity="warning">
                סוג השלוחה משתנה מ־«{initialFields.type || 'ללא'}» ל־«{composedFields.type || 'ללא'}».
                זה עלול לשנות את התנהגות הקו למתקשרים.
              </Alert>
            )}

            {needReplace && (
              <Alert severity="warning">
                חלק מהשדות הוסרו — יישמר קובץ הגדרות מלא מחדש (לא רק עדכון חלקי).
              </Alert>
            )}

            <Box>
              <Typography fontWeight={700} sx={{ mb: 1 }}>מה ישתנה</Typography>
              {changes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">אין שינויים</Typography>
              ) : (
                <Stack spacing={0.75} sx={{ maxHeight: 220, overflow: 'auto' }}>
                  {changes.map((c) => (
                    <Box
                      key={c.key}
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 1.5,
                        bgcolor: 'var(--ck-surface-2, #f5f8fc)',
                        border: '1px solid',
                        borderColor: 'divider',
                        fontSize: 13,
                      }}
                    >
                      <Typography component="span" fontWeight={700} color="primary.dark">{c.key}</Typography>
                      {c.kind === 'add' && (
                        <Typography component="span" color="success.dark"> ← חדש: {c.after || '(ריק)'}</Typography>
                      )}
                      {c.kind === 'remove' && (
                        <Typography component="span" color="error.dark"> ← יוסר ({c.before})</Typography>
                      )}
                      {c.kind === 'change' && (
                        <Typography component="span" color="text.secondary">
                          {' '}
                          {c.before || '(ריק)'} → {c.after || '(ריק)'}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            {needsConfirm && (
              <>
                <Divider />
                <Alert severity="warning">
                  זה נתיב חשוב במערכת. לאישור — הקלידו את הנתיב המדויק:
                  {' '}
                  <strong style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{displayPath(targetPath)}</strong>
                </Alert>
                <TextField
                  label="אישור נתיב"
                  value={confirmPath}
                  onChange={(e) => setConfirmPath(e.target.value)}
                  fullWidth
                  size="small"
                  inputProps={{ dir: 'ltr' }}
                  placeholder={displayPath(targetPath)}
                />
              </>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>ביטול</Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={saving}>הקודם</Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>המשך</Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isCreate ? 'צור שלוחה' : 'שמור שינויים'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
